import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  ArrowLeft, Shield, Home, Phone, Scale, Sparkles, Send, Loader2, Bot,
  CheckCircle2, XCircle, ChevronDown, ChevronUp, Star, Activity,
  Stethoscope, Plane, Users, HeartHandshake, RefreshCw, BookOpen,
  FileText, AlertTriangle, Zap, IndianRupee, Percent, Lightbulb,
  HelpCircle, BadgeCheck, TrendingUp, HeartPulse, User, Layers,
  Building2, Calendar, Clock, Leaf, Ambulance, ClipboardCheck, Banknote,
  Download, Infinity as InfinityIcon, Baby, ShieldAlert, Video, ListChecks,
  Hourglass, Gift, MapPin, Eye, X, Globe, ArrowRight
} from "lucide-react";
import axios from "axios";
import { trackAskAI, trackDocumentDownload } from "@/utils/analytics";
import SectionNav from "@/components/SectionNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEALTH_TABS = [
  { id: "overview", label: "Start Here", icon: Star, color: "from-[#D4A017] to-yellow-500" },
  { id: "variants", label: "Compare Variants", icon: Layers, color: "from-cyan-500 to-blue-500" },
  { id: "benefits-4x", label: "Key Benefits", icon: TrendingUp, color: "from-emerald-500 to-teal-500" },
  { id: "coverage", label: "Full Coverage", icon: Shield, color: "from-orange-500 to-amber-500" },
  { id: "riders", label: "Riders & Add-ons", icon: HeartHandshake, color: "from-pink-500 to-rose-400" },
  { id: "waiting-lists", label: "Waiting Periods & Lists", icon: Hourglass, color: "from-violet-500 to-purple-500" },
  { id: "exclusions", label: "What's NOT Covered", icon: XCircle, color: "from-red-500 to-rose-500" },
  { id: "premium", label: "Premium & Discounts", icon: Percent, color: "from-green-500 to-emerald-500" },
  { id: "claims", label: "Claims", icon: FileText, color: "from-cyan-500 to-[#DCE4F2]" },
  { id: "downloads", label: "Download Documents", icon: Download, color: "from-[#EDF1F8] to-cyan-600" },
  { id: "jargon", label: "Jargon & FAQs", icon: BookOpen, color: "from-purple-500 to-violet-500" },
  { id: "askme", label: "Ask AI", icon: Sparkles, color: "from-[#EDF1F8] to-[#DCE4F2]" }
];

const MATERNITY_TAB = { id: "maternity", label: "Maternity Corner", icon: Baby, color: "from-rose-500 to-pink-400" };

const BENEFIT_ICONS = {
  "shield": Shield, "trending-up": TrendingUp, "refresh-cw": RefreshCw, "badge-check": BadgeCheck, "globe": Globe
};
const COVERAGE_ICONS = {
  "building": Building2, "calendar": Calendar, "clock": Clock, "home": Home, "leaf": Leaf,
  "heart-handshake": HeartHandshake, "ambulance": Ambulance, "stethoscope": Stethoscope,
  "clipboard-check": ClipboardCheck, "banknote": Banknote, "badge-check": BadgeCheck
};
const RIDER_ICONS = {
  "infinity": InfinityIcon, "heart-pulse": HeartPulse, "activity": Activity, "baby": Baby,
  "shield-alert": ShieldAlert, "video": Video, "banknote": Banknote, "plane": Plane
};
const BENEFIT_COLORS = [
  { grad: "from-amber-500 to-yellow-500", text: "text-amber-600", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  { grad: "from-emerald-500 to-teal-500", text: "text-emerald-600", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  { grad: "from-cyan-500 to-blue-500", text: "text-cyan-600", bg: "bg-cyan-500/10", border: "border-cyan-500/30" },
  { grad: "from-pink-500 to-rose-400", text: "text-rose-600", bg: "bg-rose-500/10", border: "border-rose-500/30" }
];
const WAIT_COLORS = {
  emerald: { text: "text-emerald-600", bg: "bg-emerald-500/10", border: "border-emerald-500/40" },
  cyan: { text: "text-cyan-600", bg: "bg-cyan-500/10", border: "border-cyan-500/40" },
  amber: { text: "text-amber-600", bg: "bg-amber-500/10", border: "border-amber-500/40" },
  rose: { text: "text-rose-600", bg: "bg-rose-500/10", border: "border-rose-500/40" },
  purple: { text: "text-purple-600", bg: "bg-purple-500/10", border: "border-purple-500/40" }
};

const ExpandableCard = ({ title, icon: Icon, iconColor, badge, defaultOpen = false, children, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="glass-card overflow-hidden mb-4" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 hover:bg-[#EDF1F8]/30 transition-colors">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${iconColor} flex items-center justify-center flex-shrink-0`}>
            <Icon className="h-5 w-5 text-[#1B2A4A]" />
          </div>
          <h3 className="font-bold text-[#1B2A4A] text-base sm:text-lg text-left">{title}</h3>
          {badge && <span className="px-2 py-1 text-xs font-medium bg-[#D4A017]/15 text-[#A87608] rounded-full whitespace-nowrap">{badge}</span>}
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-[#3A4557] flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-[#3A4557] flex-shrink-0" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
            <div className="px-5 pb-5 border-t border-[#C9D4E6] pt-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Generate a branded PDF for a downloadable list
const downloadListAsPDF = (list, plan) => {
  const doc = new jsPDF();
  doc.setFillColor(13, 26, 58);
  doc.rect(0, 0, 210, 32, "F");
  doc.setTextColor(212, 160, 23);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(list.title, 14, 14);
  doc.setTextColor(184, 195, 218);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`${plan.company} — ${plan.name} | Prepared by Kavach Box`, 14, 21);
  doc.text(`Source: ${list.source_doc}`, 14, 27);
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(9);
  const descLines = doc.splitTextToSize(list.description, 180);
  doc.text(descLines, 14, 40);
  autoTable(doc, {
    startY: 40 + descLines.length * 4 + 4,
    head: [list.columns],
    body: list.items.map((item, i) => list.columns[0] === "Code" ? item.split(" — ").length === 2 ? item.split(" — ") : [String(i + 1), item] : [String(i + 1), item]),
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: [13, 26, 58], textColor: [212, 160, 23] },
    alternateRowStyles: { fillColor: [244, 242, 236] }
  });
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text("This is a simplified extract. The policy wording PDF is the final legal reference. — Kavach Box", 14, 290);
  }
  doc.save(`${list.id}-${plan.id}.pdf`);
};

// ============ TAB: OVERVIEW ============
const OverviewTab = ({ plan }) => {
  const edu = plan.health_education || {};
  return (
    <div className="space-y-6" data-testid="health-overview-tab">
      <div className="glass-card p-6 border-l-4 border-l-[#D4A017]">
        <div className="flex items-start gap-3">
          <Lightbulb className="h-6 w-6 text-[#A87608] flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-[#1B2A4A] font-bold mb-2">This plan, in plain words</h3>
            <p className="text-[#2E3B57] leading-relaxed">{plan.simple_summary}</p>
          </div>
        </div>
      </div>

      {edu.what_is && (
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
              <HelpCircle className="h-5 w-5 text-[#1B2A4A]" />
            </div>
            <h3 className="font-bold text-[#1B2A4A] text-lg">{edu.what_is.title}</h3>
          </div>
          <p className="text-[#2E3B57] leading-relaxed mb-4">{edu.what_is.simple}</p>
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <p className="text-amber-700 text-sm italic">💡 {edu.what_is.analogy}</p>
          </div>
        </div>
      )}

      {edu.misconceptions?.length > 0 && (
        <div>
          <h3 className="font-bold text-[#1B2A4A] text-lg mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            3 dangerous myths — busted
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            {edu.misconceptions.map((m, i) => (
              <div key={i} className="glass-card p-5" data-testid={`health-myth-card-${i}`}>
                <div className="flex items-start gap-2 mb-3">
                  <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 font-medium text-sm">{m.myth}</p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[#2E3B57] text-sm leading-relaxed">{m.reality}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {edu.who_needs?.length > 0 && (
        <div className="glass-card p-6">
          <h3 className="font-bold text-[#1B2A4A] text-lg mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-emerald-600" />
            Who is this plan built for?
          </h3>
          <div className="space-y-3">
            {edu.who_needs.map((w, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-[#EDF1F8]/30">
                <BadgeCheck className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[#1B2A4A] font-medium text-sm">{w.who}</p>
                  <p className="text-[#3A4557] text-xs">{w.why}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {plan.why_choose?.length > 0 && (
        <div className="glass-card p-6">
          <h3 className="font-bold text-[#1B2A4A] text-lg mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#A87608]" />
            Why advisors recommend this plan
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {plan.why_choose.map((reason, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#A87608] flex-shrink-0 mt-0.5" />
                <p className="text-[#2E3B57] text-sm">{reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ TAB: VARIANTS ============
const VariantsTab = ({ plan, currentVariantId, onSwitch }) => (
  <div className="space-y-6" data-testid="health-variants-tab">
    <div className="glass-card p-5 border-l-4 border-l-cyan-500">
      <p className="text-[#2E3B57] text-sm leading-relaxed">
        This is not one plan — it's a <span className="text-cyan-700 font-bold">family of {plan.plan_variants?.length || 8} variants</span> built
        on the same engine. From budget (Lite) to borderless (Global Plus), your advisor matches the variant to your budget and life stage.
        Every variant below is clearly bifurcated — what it adds, and what to watch out for.
      </p>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      {plan.plan_variants?.map((v, i) => (
        <div key={v.id} className={`glass-card p-5 flex flex-col ${v.highlight ? "border-[#D4A017]/50 bg-[#D4A017]/5" : ""}`} data-testid={`health-variant-${v.id}`}>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h3 className={`font-bold text-base ${v.highlight ? "text-[#A87608]" : "text-[#1B2A4A]"}`}>{v.name}</h3>
            <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${v.highlight ? "bg-[#D4A017]/20 text-[#A87608]" : "bg-[#EDF1F8] text-[#2E3B57]"}`}>{v.badge}</span>
          </div>
          <p className="text-[#2E3B57] text-sm leading-relaxed mb-3">{v.description}</p>
          <div className="space-y-1.5 mb-3">
            {v.key_features?.map((f, j) => (
              <div key={j} className="flex items-start gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-[#3A4557] text-xs">{f}</p>
              </div>
            ))}
          </div>
          {v.watch_out && (
            <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-2">
              <p className="text-amber-700/90 text-xs"><span className="font-bold">Watch out:</span> {v.watch_out}</p>
            </div>
          )}
          <p className="text-cyan-600 text-xs font-medium mt-auto">Best for: {v.best_for}</p>
          {v.id === currentVariantId ? (
            <span className="mt-3 inline-flex items-center gap-1.5 text-emerald-600 text-xs font-bold" data-testid={`health-variant-current-${v.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5" /> Currently viewing
            </span>
          ) : (
            <Button size="sm" variant="outline" onClick={() => onSwitch(v.id)} className="mt-3 border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A] text-xs h-8 self-start" data-testid={`health-variant-switch-${v.id}`}>
              View this variant <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          )}
        </div>
      ))}
    </div>

    {plan.variant_matrix && (
      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
          <Scale className="h-5 w-5 text-cyan-600" />
          Variant-by-variant bifurcation
        </h3>
        <p className="text-[#3A4557] text-xs mb-4">{plan.variant_matrix.note}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="health-variant-matrix">
            <thead>
              <tr className="border-b border-[#C9D4E6]">
                <th className="text-left py-2 pr-3 text-[#3A4557] font-medium sticky left-0 bg-white min-w-[160px]">Feature</th>
                {plan.variant_matrix.columns.map((c, i) => (
                  <th key={i} className="text-center py-2 px-2 text-[#A87608] font-bold whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {plan.variant_matrix.rows.map((row, i) => (
                <tr key={i} className="border-b border-[#C9D4E6]/50 last:border-0">
                  <td className="py-2.5 pr-3 text-[#2E3B57] sticky left-0 bg-white">{row.feature}</td>
                  {row.values.map((val, j) => (
                    <td key={j} className={`py-2.5 px-2 text-center whitespace-nowrap ${val.startsWith("✓") ? "text-emerald-600 font-medium" : val === "✗" ? "text-[#3A4557]" : "text-[#2E3B57]"}`}>{val}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

// ============ TAB: 4X BENEFITS ============
const BenefitsTab = ({ plan, variant, benefits, story }) => (
  <div className="space-y-6" data-testid="health-benefits-tab">
    <div className="glass-card p-5 border-l-4 border-l-emerald-500">
      <p className="text-[#2E3B57] text-sm leading-relaxed">
        {story ? (
          <>These benefits are the engine of <span className="text-emerald-700 font-bold">{variant?.name}</span> — the reason
          your cover behaves far bigger than what you pay for. Each one decoded with real-life examples.</>
        ) : (
          <>These are the benefits included in <span className="text-emerald-700 font-bold">{variant?.name}</span> —
          decoded from the policy wording with real-life examples. Note: this variant does not include the 4X multiplier
          benefits (Secure/Plus) — see the Compare Variants tab if you want them.</>
        )}
      </p>
    </div>

    {benefits?.map((b, i) => {
      const Icon = BENEFIT_ICONS[b.icon] || Shield;
      const c = BENEFIT_COLORS[i % BENEFIT_COLORS.length];
      return (
        <div key={b.id} className="glass-card overflow-hidden" data-testid={`health-benefit-${b.id}`}>
          <div className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-4">
              <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${c.grad} flex items-center justify-center flex-shrink-0`}>
                <Icon className="h-6 w-6 text-[#1B2A4A]" />
              </div>
              <div className="flex-1">
                <span className={`text-xs font-bold ${c.text}`}>BENEFIT {i + 1} OF {benefits.length}</span>
                <h3 className="font-bold text-[#1B2A4A] text-lg">{b.name}</h3>
              </div>
              <div className={`px-3 py-1.5 rounded-full ${c.bg} border ${c.border} self-start sm:self-center`}>
                <span className={`text-sm font-bold ${c.text}`}>{b.payout}</span>
              </div>
            </div>
            <p className="text-[#2E3B57] leading-relaxed mb-4">{b.description}</p>
            {b.example && (
              <div className={`p-4 rounded-xl ${c.bg} border ${c.border} mb-4`}>
                <p className={`text-xs font-bold ${c.text} mb-2 uppercase tracking-wide`}>Real-life example</p>
                <p className="text-[#2E3B57] text-sm leading-relaxed mb-2">{b.example.story}</p>
                <div className="flex items-center gap-2">
                  <IndianRupee className={`h-4 w-4 ${c.text}`} />
                  <span className={`font-bold text-sm ${c.text}`}>{b.example.payout}</span>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {b.details?.map((d, j) => (
                <div key={j} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[#3A4557] text-sm">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    })}

    {story && (
      <div className="glass-card p-6 bg-gradient-to-br from-[#D4A017]/5 to-emerald-500/5" data-testid="health-multiplier-story">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
          <Zap className="h-5 w-5 text-[#A87608]" />
          {story.title}
        </h3>
        <p className="text-[#3A4557] text-sm mb-5">{story.note}</p>
        <div className="space-y-0">
          {story.steps.map((s, i) => (
            <div key={i} className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${i === 0 ? "bg-[#EDF1F8] border border-[#D4A017]/40" : "bg-gradient-to-br from-[#D4A017] to-yellow-600"}`}>
                  <span className="text-[#1B2A4A] font-bold text-xs">{i === 0 ? "₹" : `+`}</span>
                </div>
                {i < story.steps.length - 1 && <div className="w-0.5 flex-1 bg-[#D4A017]/30 my-1" />}
              </div>
              <div className="pb-5">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[#1B2A4A] font-bold text-sm">{s.label}</p>
                  <span className="text-[#A87608] font-bold text-sm">{s.amount}</span>
                </div>
                <p className="text-[#3A4557] text-xs">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
          <p className="text-emerald-700 text-xs leading-relaxed">{story.footnote}</p>
        </div>
      </div>
    )}
  </div>
);

// ============ TAB: FULL COVERAGE ============
const CoverageTab = ({ plan, vd, variant, subLimits }) => (
  <div className="space-y-6" data-testid="health-coverage-tab">
    <div className="glass-card p-5 border-l-4 border-l-orange-500">
      <p className="text-[#2E3B57] text-sm leading-relaxed">
        Everything the base policy pays for — decoded from the 60+ page wording. Items marked with
        <span className="text-amber-600 font-bold"> ⚑ sub-limit</span> have a cap you must know about (all caps are honestly listed at the bottom).
      </p>
    </div>

    {vd?.room_note && (
      <div className="glass-card p-5 border-l-4 border-l-amber-500 flex items-start gap-3" data-testid="health-room-note">
        <Building2 className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-[#1B2A4A] font-bold text-sm mb-1">Room rules on {variant?.name}</p>
          <p className="text-[#2E3B57] text-sm leading-relaxed">{vd.room_note}</p>
        </div>
      </div>
    )}

    {vd?.global_section && (
      <div className="glass-card p-6 border border-cyan-500/30" data-testid="health-global-section">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
          <Globe className="h-5 w-5 text-cyan-600" />
          {vd.global_section.title}
        </h3>
        <p className="text-cyan-700 text-sm font-medium mb-3">{vd.global_section.scope}</p>
        <div className="space-y-2 mb-3">
          {vd.global_section.points.map((p, i) => (
            <div key={i} className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-cyan-600 flex-shrink-0 mt-0.5" />
              <p className="text-[#2E3B57] text-sm">{p}</p>
            </div>
          ))}
        </div>
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/25 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-amber-800 text-xs leading-relaxed">{vd.global_section.watch}</p>
        </div>
      </div>
    )}

    <div className="grid sm:grid-cols-2 gap-4">
      {plan.base_coverage?.map((c, i) => {
        const Icon = COVERAGE_ICONS[c.icon] || Shield;
        return (
          <div key={c.id} className="glass-card p-5" data-testid={`health-coverage-${c.id}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0">
                <Icon className="h-5 w-5 text-[#1B2A4A]" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-[#1B2A4A] font-bold text-sm flex items-center gap-1.5">
                  {c.name}
                  {c.sub_limit_flag && <span className="text-amber-600 text-xs" title="Has a sub-limit">⚑</span>}
                </h4>
                <p className="text-[#A87608] text-xs font-medium">{c.limit}</p>
              </div>
            </div>
            <p className="text-[#2E3B57] text-sm leading-relaxed mb-2">{c.simple_explanation}</p>
            <div className="space-y-1">
              {c.details?.map((d, j) => (
                <div key={j} className="flex items-start gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[#3A4557] text-xs">{d}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>

    {subLimits?.length > 0 && (
      <div className="glass-card p-6" data-testid="health-sublimits-section">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          Sub-limits & fine print — the honest advisor list
        </h3>
        <p className="text-[#3A4557] text-sm mb-4">Every cap, condition and trade-off in this policy, so nothing surprises you at claim time.</p>
        <div className="space-y-3">
          {subLimits.map((s, i) => (
            <div key={i} className={`p-4 rounded-xl border flex items-start gap-3 ${s.severity === "warn" ? "bg-amber-500/5 border-amber-500/25" : "bg-[#EDF1F8]/30 border-[#C9D4E6]"}`}>
              {s.severity === "warn"
                ? <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                : <Lightbulb className="h-4 w-4 text-cyan-600 flex-shrink-0 mt-0.5" />}
              <div>
                <p className="text-[#1B2A4A] font-medium text-sm mb-0.5">{s.title}</p>
                <p className="text-[#3A4557] text-xs leading-relaxed">{s.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// ============ TAB: RIDERS ============
const RidersTab = ({ plan, riders, levers }) => (
  <div className="space-y-4" data-testid="health-riders-tab">
    <div className="glass-card p-5 border-l-4 border-l-pink-500">
      <p className="text-[#2E3B57] text-sm leading-relaxed">
        The base plan is the chassis — these <span className="text-rose-700 font-bold">{riders?.length} add-ons</span> are the customizations
        available with this variant. Each one solves a specific gap (maternity, diabetes, unlimited cover, OPD costs...).
        Expand any card for the full expert breakdown, including waiting periods and honest watch-outs.
        Final add-on availability is confirmed at purchase — your advisor verifies it for your chosen variant.
      </p>
    </div>
    {riders?.map((r, i) => {
      const Icon = RIDER_ICONS[r.icon] || HeartHandshake;
      return (
        <ExpandableCard
          key={r.id}
          title={r.name}
          icon={Icon}
          iconColor="from-pink-500 to-rose-400"
          badge={r.limit}
          defaultOpen={i === 0}
          testId={`health-rider-${r.id}`}
        >
          <p className="text-[#2E3B57] text-sm leading-relaxed mb-3">{r.description}</p>
          <div className="p-3.5 rounded-xl bg-[#EDF1F8]/40 border border-[#C9D4E6] mb-4">
            <p className="text-xs font-bold text-rose-700 mb-1 uppercase tracking-wide">Why it matters</p>
            <p className="text-[#2E3B57] text-sm leading-relaxed">{r.simple_explanation}</p>
          </div>
          <div className="space-y-2 mb-4">
            {r.details?.map((d, j) => (
              <div key={j} className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-[#3A4557] text-sm">{d}</p>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/25">
              <p className="text-violet-700 text-xs font-bold mb-1">⏳ Waiting period</p>
              <p className="text-[#2E3B57] text-xs">{r.waiting_period}</p>
            </div>
            <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/25">
              <p className="text-cyan-700 text-xs font-bold mb-1">✓ Best for</p>
              <p className="text-[#2E3B57] text-xs">{r.best_for}</p>
            </div>
          </div>
          {r.watch_out && (
            <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/25 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800 text-xs leading-relaxed">{r.watch_out}</p>
            </div>
          )}
          {r.uin && <p className="text-[#3A4557] text-[10px] mt-3">UIN: {r.uin}</p>}
        </ExpandableCard>
      );
    })}

    {plan.permutations && (
      <div className="glass-card p-6" data-testid="health-permutations-section">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-cyan-600" />
          {plan.permutations.title}
        </h3>
        <p className="text-[#3A4557] text-sm mb-4">Beyond add-ons, these built-in levers change premium and coverage. Every permutation, explained.</p>
        <div className="space-y-3">
          {levers?.map((l, i) => (
            <div key={i} className="p-4 rounded-xl bg-[#EDF1F8]/30 border border-[#C9D4E6]">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <p className="text-[#1B2A4A] font-bold text-sm">{l.name}</p>
                <span className="px-2 py-0.5 text-[10px] font-medium bg-cyan-500/15 text-cyan-700 rounded-full">{l.options}</span>
              </div>
              <p className="text-[#2E3B57] text-xs leading-relaxed mb-1">{l.explanation}</p>
              {l.warning && <p className="text-amber-700/90 text-xs leading-relaxed"><span className="font-bold">⚠ </span>{l.warning}</p>}
            </div>
          ))}
        </div>
        {plan.permutations.zones && (
          <div className="mt-4">
            <p className="text-[#1B2A4A] font-medium text-sm mb-1 flex items-center gap-2"><MapPin className="h-4 w-4 text-emerald-600" /> City tiers (premium only — NO zonal co-pay)</p>
            <p className="text-[#3A4557] text-xs mb-3">{plan.permutations.zones.note}</p>
            <div className="space-y-1.5">
              {plan.permutations.zones.tiers.map((t, i) => (
                <div key={i} className="flex items-start gap-3 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-[#EDF1F8] text-[#2E3B57] font-medium w-14 text-center flex-shrink-0">{t.tier}</span>
                  <span className="text-[#3A4557]">{t.cities}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

// ============ TAB: WAITING PERIODS & LISTS ============
const WaitingListsTab = ({ plan }) => {
  const [viewList, setViewList] = useState(null);
  return (
    <div className="space-y-6" data-testid="health-waiting-tab">
      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
          <Hourglass className="h-5 w-5 text-violet-600" />
          The waiting period timeline
        </h3>
        <p className="text-[#3A4557] text-sm mb-5">Waiting periods are when the policy says "not yet". Know them before buying — they are the #1 cause of rejected claims.</p>
        <div className="space-y-3">
          {plan.waiting_periods_detail?.map((w, i) => {
            const c = WAIT_COLORS[w.color] || WAIT_COLORS.cyan;
            return (
              <div key={i} className={`p-4 rounded-xl border ${c.bg} ${c.border} flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4`} data-testid={`health-waiting-${i}`}>
                <span className={`font-bold text-sm ${c.text} w-24 flex-shrink-0`}>{w.period}</span>
                <div>
                  <p className="text-[#1B2A4A] font-bold text-sm mb-0.5">{w.title}</p>
                  <p className="text-[#2E3B57] text-xs leading-relaxed">{w.simple_explanation}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="glass-card p-6" data-testid="health-lists-section">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-[#A87608]" />
          Every list in the policy — explained & downloadable
        </h3>
        <p className="text-[#3A4557] text-sm mb-5">
          The policy wording contains critical lists (specified diseases, covered consumables, critical illnesses...).
          We've extracted each one — read it here, or download as a clean PDF to share with clients.
        </p>
        <div className="space-y-4">
          {plan.downloadable_lists?.map((list) => (
            <div key={list.id} className="p-4 rounded-xl bg-[#EDF1F8]/30 border border-[#C9D4E6]" data-testid={`health-list-${list.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-[#1B2A4A] font-bold text-sm">{list.title}</p>
                    <span className="px-2 py-0.5 text-[10px] font-bold bg-[#D4A017]/15 text-[#A87608] rounded-full">{list.badge}</span>
                  </div>
                  <p className="text-[#2E3B57] text-xs leading-relaxed">{list.description}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setViewList(viewList === list.id ? null : list.id)}
                    className="border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A] text-xs h-8"
                    data-testid={`health-list-view-${list.id}`}
                  >
                    <Eye className="h-3.5 w-3.5 mr-1.5" />
                    {viewList === list.id ? "Hide" : "View"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => downloadListAsPDF(list, plan)}
                    className="btn-gold text-xs h-8"
                    data-testid={`health-list-download-${list.id}`}
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    PDF
                  </Button>
                </div>
              </div>
              <p className="text-cyan-700/80 text-[11px] italic mb-2">💡 {list.why_matters}</p>
              <p className="text-[#3A4557] text-[10px]">Source: {list.source_doc}</p>
              <AnimatePresence>
                {viewList === list.id && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                    <div className="mt-3 pt-3 border-t border-[#C9D4E6] grid sm:grid-cols-2 gap-x-6 gap-y-1.5 max-h-80 overflow-y-auto">
                      {list.items.map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="text-[#A87608] text-[10px] font-bold mt-0.5 w-5 flex-shrink-0">{i + 1}.</span>
                          <p className="text-[#2E3B57] text-xs leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ TAB: EXCLUSIONS ============
const ExclusionsTab = ({ plan }) => (
  <div className="space-y-6" data-testid="health-exclusions-tab">
    {plan.exclusions_note && (
      <div className="glass-card p-5 border-l-4 border-l-emerald-500 flex items-start gap-3">
        <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
        <p className="text-[#2E3B57] text-sm leading-relaxed">{plan.exclusions_note}</p>
      </div>
    )}
    <div className="glass-card p-5 border-l-4 border-l-red-500">
      <p className="text-[#2E3B57] text-sm leading-relaxed">
        <span className="text-[#1B2A4A] font-bold">Honest advisor talk:</span> every policy has boundaries. Knowing them upfront
        means zero surprises at claim time. Here they all are, in plain language (the official IRDAI exclusion codes are downloadable in the Lists tab):
      </p>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      {plan.exclusions?.map((e, i) => (
        <div key={i} className="glass-card p-5" data-testid={`health-exclusion-card-${i}`}>
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <h4 className="text-[#1B2A4A] font-bold text-sm">{e.title}</h4>
          </div>
          <p className="text-[#3A4557] text-sm leading-relaxed">{e.simple_explanation}</p>
        </div>
      ))}
    </div>
  </div>
);

// ============ TAB: PREMIUM & DISCOUNTS ============
const PremiumTab = ({ plan, vd }) => {
  const prem = plan.premium_info || {};
  const care = plan.policy_care || {};
  const samples = vd?.sample_premiums?.length ? vd.sample_premiums : null;
  return (
    <div className="space-y-6" data-testid="health-premium-tab">
      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-2 flex items-center gap-2">
          <IndianRupee className="h-5 w-5 text-[#A87608]" />
          How the premium works
        </h3>
        <p className="text-[#2E3B57] text-sm mb-4">{prem.note}</p>
        {samples ? (
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            {samples.map((s, i) => (
              <div key={i} className="p-4 rounded-xl bg-[#D4A017]/10 border border-[#D4A017]/30 text-center">
                <p className="text-[#3A4557] text-xs mb-1">{s.label}</p>
                <p className="text-[#A87608] font-bold text-sm">{s.premium}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#3A4557] text-sm italic mb-4" data-testid="health-premium-no-sample-note">
            Premium illustrations for this variant are prepared by your advisor based on your age, city and family size — ask for a personalized quote.
          </p>
        )}
        <p className="text-[#3A4557] text-sm mb-2"><span className="text-[#1B2A4A] font-medium">Payment modes:</span> {prem.payment_modes}</p>
        {prem.gst_note && <p className="text-[#3A4557] text-xs italic">{prem.gst_note}</p>}
      </div>

      <div className="glass-card p-6" data-testid="health-discounts-section">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
          <Gift className="h-5 w-5 text-emerald-600" />
          Every discount you can stack
        </h3>
        <p className="text-[#3A4557] text-sm mb-4">Smart buyers combine 3-4 of these — your advisor structures the purchase to maximize them.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {plan.discounts?.map((d, i) => (
            <div key={i} className="p-4 rounded-xl bg-[#EDF1F8]/30 border border-[#C9D4E6]">
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-[#1B2A4A] font-medium text-sm">{d.name}</p>
                <span className="px-2 py-0.5 text-xs font-bold bg-emerald-500/15 text-emerald-600 rounded-full whitespace-nowrap">{d.discount}</span>
              </div>
              <p className="text-[#3A4557] text-xs">{d.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-cyan-600" />
          Who can be covered & eligibility
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="p-3 rounded-xl bg-[#EDF1F8]/30"><p className="text-[#3A4557] text-xs mb-1">Entry age</p><p className="text-[#1B2A4A] text-sm font-medium">{plan.policy_options?.entry_age}</p></div>
          <div className="p-3 rounded-xl bg-[#EDF1F8]/30"><p className="text-[#3A4557] text-xs mb-1">Family floater capacity</p><p className="text-[#1B2A4A] text-sm font-medium">{plan.policy_options?.max_members_floater}</p></div>
          <div className="p-3 rounded-xl bg-[#EDF1F8]/30"><p className="text-[#3A4557] text-xs mb-1">Policy term</p><p className="text-[#1B2A4A] text-sm font-medium">{plan.policy_options?.policy_term}</p></div>
          <div className="p-3 rounded-xl bg-[#EDF1F8]/30"><p className="text-[#3A4557] text-xs mb-1">Medical tests</p><p className="text-[#1B2A4A] text-sm font-medium">{plan.policy_options?.medical_tests}</p></div>
        </div>
      </div>

      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-4">Policy care — the fine print, simplified</h3>
        <div className="space-y-3">
          {[["Free look", care.free_look], ["Grace period", care.grace_period], ["Renewal", care.renewal], ["Moratorium", care.moratorium]].map(([label, val], i) => val && (
            <div key={i} className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-[#2E3B57] text-sm"><span className="text-[#1B2A4A] font-medium">{label}: </span>{val}</p>
            </div>
          ))}
          {care.tax_note && (
            <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
              <Lightbulb className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800 text-sm">{care.tax_note}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ TAB: CLAIMS ============
const ClaimsTab = ({ plan }) => {
  const proc = plan.claim_process || {};
  return (
    <div className="space-y-6" data-testid="health-claims-tab">
      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-6">How to claim — step by step</h3>
        <div className="space-y-6">
          {proc.steps?.map((s, i) => (
            <div key={i} className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#D4A017] to-yellow-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#1B2A4A] font-bold">{s.step}</span>
                </div>
                {i < proc.steps.length - 1 && <div className="w-0.5 flex-1 bg-[#EDF1F8] mt-2" />}
              </div>
              <div className="pb-2">
                <h4 className="text-[#1B2A4A] font-bold text-sm mb-1">{s.title}</h4>
                <p className="text-[#3A4557] text-sm leading-relaxed">{s.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {plan.claim_settlement_time && (
        <div className="glass-card p-5 flex items-start gap-3 border-l-4 border-l-emerald-500">
          <Clock className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-[#1B2A4A] font-bold text-sm mb-1">Settlement promise (by IRDAI regulation)</h4>
            <p className="text-[#2E3B57] text-sm">{plan.claim_settlement_time}</p>
          </div>
        </div>
      )}
      {proc.advisor_tip && (
        <div className="glass-card p-5 flex items-start gap-3 border-l-4 border-l-[#D4A017]">
          <Lightbulb className="h-6 w-6 text-[#A87608] flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-[#1B2A4A] font-bold text-sm mb-1">Advisor's tip</h4>
            <p className="text-[#2E3B57] text-sm">{proc.advisor_tip}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ TAB: DOWNLOADS ============
const DownloadsTab = ({ plan, vd, variant }) => {
  const variantDocNames = vd?.document_names || [];
  const variantDocs = (plan.documents || []).filter(d => variantDocNames.includes(d.name));
  const restDocs = (plan.documents || []).filter(d => !variantDocNames.includes(d.name));
  const categories = [...new Set(restDocs.map(d => d.category))];

  const DocLink = ({ doc, highlighted, testId }) => (
    <a
      href={doc.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackDocumentDownload && trackDocumentDownload(plan.id, doc.name)}
      className={`p-4 rounded-xl border transition-colors group flex items-start gap-3 ${highlighted ? "bg-[#D4A017]/5 border-[#D4A017]/40 hover:border-[#D4A017]" : "bg-[#EDF1F8]/30 border-[#C9D4E6] hover:border-[#D4A017]/50"}`}
      data-testid={testId}
    >
      <div className="h-10 w-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0">
        <FileText className="h-5 w-5 text-red-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[#1B2A4A] font-medium text-sm group-hover:text-[#A87608] transition-colors">{doc.name}</p>
        <p className="text-[#3A4557] text-xs leading-relaxed mt-0.5">{doc.description}</p>
        <span className="inline-flex items-center gap-1 text-[#A87608] text-xs font-medium mt-1.5">
          <Download className="h-3 w-3" /> Download PDF
        </span>
      </div>
    </a>
  );

  return (
    <div className="space-y-6" data-testid="health-downloads-tab">
      <div className="glass-card p-5 border-l-4 border-l-cyan-500">
        <p className="text-[#2E3B57] text-sm leading-relaxed">
          Everything on this page is decoded from these <span className="text-cyan-700 font-bold">original insurer documents</span>.
          Download and keep them — the master policy wording is the final legal reference for every variant, including {variant?.name}.
        </p>
      </div>

      {variantDocs.length > 0 && (
        <div className="glass-card p-6 border border-[#D4A017]/30" data-testid="health-variant-docs-section">
          <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
            <Star className="h-5 w-5 text-[#A87608]" />
            Specific to {variant?.name}
          </h3>
          <p className="text-[#3A4557] text-xs mb-4">Documents published for this variant — read these first.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {variantDocs.map((doc, i) => (
              <DocLink key={i} doc={doc} highlighted testId={`health-variant-doc-${i}`} />
            ))}
          </div>
        </div>
      )}

      {categories.map(cat => (
        <div key={cat} className="glass-card p-6">
          <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#A87608]" />
            {cat} Documents
          </h3>
          {cat === "Base Plan" && (
            <p className="text-[#3A4557] text-xs mb-4">The master policy wording legally governs all 8 variants — waiting periods, lists and exclusions are identical across variants.</p>
          )}
          <div className={`grid sm:grid-cols-2 gap-3 ${cat !== "Base Plan" ? "mt-4" : ""}`}>
            {restDocs.filter(d => d.category === cat).map((doc, i) => (
              <DocLink key={i} doc={doc} testId={`health-doc-${i}-${cat.toLowerCase().replace(/\s/g, '-')}`} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ TAB: JARGON & FAQs ============
const JargonTab = ({ plan }) => {
  const [openFaq, setOpenFaq] = useState(null);
  return (
    <div className="space-y-6" data-testid="health-jargon-tab">
      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-purple-600" />
          Jargon Buster
        </h3>
        <p className="text-[#3A4557] text-sm mb-4">Insurance words, translated into human language.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {plan.jargon_buster?.map((j, i) => (
            <div key={i} className="p-4 rounded-xl bg-[#EDF1F8]/30">
              <p className="text-purple-700 font-bold text-sm mb-1">{j.term}</p>
              <p className="text-[#2E3B57] text-xs leading-relaxed">{j.meaning}</p>
            </div>
          ))}
        </div>
      </div>

      {plan.faqs?.length > 0 && (
        <div className="glass-card p-6">
          <h3 className="font-bold text-[#1B2A4A] text-lg mb-4 flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-cyan-600" />
            Questions everyone asks
          </h3>
          <div className="space-y-3">
            {plan.faqs.map((f, i) => (
              <div key={i} className="border border-[#C9D4E6] rounded-xl overflow-hidden" data-testid={`health-faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left hover:bg-[#EDF1F8]/30 transition-colors">
                  <span className="text-[#1B2A4A] font-medium text-sm pr-4">{f.q}</span>
                  {openFaq === i ? <ChevronUp className="h-4 w-4 text-[#3A4557] flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-[#3A4557] flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4">
                    <p className="text-[#2E3B57] text-sm leading-relaxed">{f.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ TAB: ASK AI ============
const AskAITab = ({ plan, variant }) => {
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const endRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/plans/${plan.id}/ask/suggestions`)
      .then(r => setSuggestions(r.data.suggestions || []))
      .catch(() => {});
  }, [plan.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history]);

  const ask = async (q = question) => {
    if (!q.trim() || loading) return;
    setHistory(prev => [...prev, { type: "user", text: q }]);
    setQuestion("");
    setLoading(true);
    trackAskAI(plan.id, q);
    try {
      const res = await axios.post(`${API}/plans/${plan.id}/ask`, {
        plan_id: plan.id,
        question: variant ? `[The user is asking about the "${variant.name}" variant — answer specifically for it] ${q}` : q,
        session_id: sessionId
      });
      setSessionId(res.data.session_id);
      setHistory(prev => [...prev, { type: "ai", text: res.data.answer }]);
    } catch {
      setHistory(prev => [...prev, { type: "ai", text: "Sorry, I couldn't process that question. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass-card p-4 sm:p-6" data-testid="health-askme-tab">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#EDF1F8] to-[#DCE4F2] flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-[#A87608]" />
        </div>
        <div>
          <h3 className="font-bold text-[#1B2A4A]">Ask anything about this plan</h3>
          <p className="text-[#3A4557] text-xs">AI advisor trained on the full policy wording — answering for {variant ? variant.name : "this plan"}</p>
        </div>
      </div>

      {history.length === 0 && suggestions.length > 0 && (
        <div className="mb-4">
          <p className="text-[#3A4557] text-xs mb-2">Popular questions:</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => ask(s)} className="px-3 py-1.5 rounded-full bg-[#EDF1F8]/50 border border-[#C9D4E6] text-[#2E3B57] text-xs hover:border-[#D4A017]/50 hover:text-[#1B2A4A] transition-colors" data-testid={`health-ask-suggestion-${i}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4 mb-4 max-h-[45vh] overflow-y-auto">
        {history.map((msg, i) => (
          <div key={i} className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`} data-testid={msg.type === "ai" ? `health-ask-answer-${i}` : `health-ask-question-${i}`}>
            <div className={`max-w-[85%] p-3 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
              msg.type === "user" ? "bg-[#D4A017]/15 text-[#A87608] border border-[#D4A017]/30" : "bg-[#EDF1F8]/40 text-[#2E3B57] border border-[#C9D4E6]"
            }`}>
              {msg.type === "ai" && <Bot className="h-4 w-4 text-[#A87608] mb-1" />}
              {msg.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#EDF1F8]/40 border border-[#C9D4E6] p-3 rounded-xl">
              <Loader2 className="h-5 w-5 text-[#A87608] animate-spin" />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="e.g. Which variant is right for my family?"
          className="flex-1 px-4 py-2.5 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm placeholder:text-[#3A4557] focus:outline-none focus:border-[#D4A017]/50"
          data-testid="health-ask-input"
        />
        <Button onClick={() => ask()} disabled={loading || !question.trim()} className="btn-gold px-4" data-testid="health-ask-submit-button">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ============ VARIANT CHOOSER (Step 1) ============
// ============ TAB: MATERNITY CORNER (plan-specific) ============
const MaternityTab = ({ plan }) => {
  const m = plan.maternity_spotlight;
  if (!m) return null;
  return (
    <div className="space-y-6" data-testid="health-maternity-tab">
      <div className="glass-card p-6 border-l-4 border-l-rose-400">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-2 flex items-center gap-2">
          <Baby className="h-5 w-5 text-rose-600" />
          {m.title}
        </h3>
        <p className="text-[#2E3B57] text-sm leading-relaxed">{m.intro}</p>
        {m.eligibility_note && (
          <div className="mt-3 p-3 rounded-xl bg-rose-500/10 border border-rose-500/30" data-testid="maternity-eligibility-note">
            <p className="text-rose-800 text-xs leading-relaxed"><span className="font-bold">Who can use this: </span>{m.eligibility_note}</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {m.highlights?.map((h, i) => (
          <div key={i} className="glass-card p-4 text-center" data-testid={`maternity-highlight-${i}`}>
            <p className="text-rose-700 font-bold text-lg leading-tight">{h.value}</p>
            <p className="text-[#1B2A4A] text-xs font-medium mt-1">{h.label}</p>
            {h.note && <p className="text-[#3A4557] text-[10px] mt-0.5 leading-snug">{h.note}</p>}
          </div>
        ))}
      </div>

      <div>
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1">The complete maternity benefit stack</h3>
        <p className="text-[#3A4557] text-sm mb-4">Every maternity-linked cover in this policy — exact limits by Sum Insured, waiting periods and the fine print.</p>
        {m.covers?.map((c, i) => (
          <ExpandableCard key={c.id} title={c.name} icon={Baby} iconColor="from-rose-500 to-pink-400" badge={c.badge} defaultOpen={i === 0} testId={`maternity-cover-${c.id}`}>
            <p className="text-[#2E3B57] text-sm leading-relaxed mb-3">{c.simple_explanation}</p>
            {c.limit_rows?.length > 0 && (
              <div className="rounded-xl overflow-hidden border border-[#C9D4E6] mb-3">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#EDF1F8]/60">
                      <th className="text-left p-2.5 text-[#A87608] font-bold">Sum Insured</th>
                      <th className="text-left p-2.5 text-[#A87608] font-bold">Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.limit_rows.map((r, j) => (
                      <tr key={j} className="border-t border-[#C9D4E6]">
                        <td className="p-2.5 text-[#2E3B57]">{r.si}</td>
                        <td className="p-2.5 text-[#1B2A4A] font-medium">{r.limit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <ul className="space-y-1.5 mb-3">
              {c.details?.map((d, j) => (
                <li key={j} className="flex items-start gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span className="text-[#2E3B57] text-xs leading-relaxed">{d}</span>
                </li>
              ))}
            </ul>
            {c.watch_out && (
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-amber-800/90 text-xs leading-relaxed">{c.watch_out}</p>
              </div>
            )}
          </ExpandableCard>
        ))}
      </div>

      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
          <Hourglass className="h-5 w-5 text-violet-600" />
          Maternity waiting periods — the clocks that matter
        </h3>
        <p className="text-[#3A4557] text-sm mb-4">On this plan, a higher Sum Insured means a shorter wait. Plan the policy BEFORE planning the baby.</p>
        <div className="space-y-3">
          {m.waiting_timeline?.map((w, i) => {
            const c = WAIT_COLORS[w.color] || WAIT_COLORS.rose;
            return (
              <div key={i} className={`p-4 rounded-xl border ${c.bg} ${c.border} flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4`} data-testid={`maternity-waiting-${i}`}>
                <span className={`font-bold text-sm ${c.text} w-28 flex-shrink-0`}>{w.period}</span>
                <div>
                  <p className="text-[#1B2A4A] font-bold text-sm mb-0.5">{w.title}</p>
                  <p className="text-[#2E3B57] text-xs leading-relaxed">{w.explanation}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="glass-card p-6">
        <h3 className="font-bold text-[#1B2A4A] text-lg mb-1 flex items-center gap-2">
          <XCircle className="h-5 w-5 text-red-600" />
          What maternity does NOT cover here
        </h3>
        <p className="text-[#3A4557] text-sm mb-4">The honest list — so there are zero surprises in the labour room.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {m.not_covered?.map((e, i) => (
            <div key={i} className="p-4 rounded-xl bg-red-500/5 border border-red-500/20" data-testid={`maternity-not-covered-${i}`}>
              <p className="text-[#1B2A4A] font-bold text-sm mb-1">{e.title}</p>
              <p className="text-[#3A4557] text-xs leading-relaxed">{e.explanation}</p>
            </div>
          ))}
        </div>
      </div>

      {m.advisor_verdict && (
        <div className="glass-card p-5 border-l-4 border-l-[#D4A017] flex items-start gap-3" data-testid="maternity-advisor-verdict">
          <Lightbulb className="h-6 w-6 text-[#A87608] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[#1B2A4A] font-bold text-sm mb-1">Advisor's verdict on the maternity benefit</p>
            <p className="text-[#2E3B57] text-sm leading-relaxed">{m.advisor_verdict}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const VariantChooser = ({ plan, onChoose }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] pb-16" data-testid="health-variant-chooser">
      <header className="glass-nav sticky top-2 mx-2 sm:mx-6 z-50 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between rounded-xl sm:rounded-2xl">
        <div className="flex items-center gap-1 sm:gap-3">
          <Button variant="ghost" onClick={() => navigate("/")} className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40 px-2 sm:px-3" data-testid="health-chooser-home-button">
            <Home className="h-4 w-4" />
          </Button>
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40 px-2 sm:px-3" data-testid="health-chooser-back-button">
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline ml-2">Back</span>
          </Button>
        </div>
        <a href="tel:+919510020907">
          <Button className="btn-gold h-8 sm:h-10 px-3 sm:px-5 text-xs sm:text-sm" data-testid="health-chooser-call-button">
            <Phone className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Talk to Advisor</span>
          </Button>
        </a>
      </header>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 sm:p-8 mb-6">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-white p-2 flex items-center justify-center flex-shrink-0">
              <img src={plan.company_logo} alt={plan.company} className="max-h-full max-w-full object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-700 text-xs font-bold">HEALTH INSURANCE</span>
                <span className="text-[#3A4557] text-xs">{plan.company_short || plan.company}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1B2A4A] mb-1" data-testid="health-chooser-plan-name">{plan.name}</h1>
              <p className="text-[#A87608] text-sm font-medium">{plan.tagline}</p>
            </div>
          </div>
          <div className="mt-5 p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-start gap-3">
            <Layers className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[#1B2A4A] font-bold text-sm mb-1">Step 1 — Choose the variant you want to explore</p>
              <p className="text-[#2E3B57] text-xs leading-relaxed">
                This is a family of {plan.plan_variants?.length || 8} variants on one policy wording. Pick one below and the
                entire page — benefits, room rules, riders, premiums and documents — will be shown for THAT variant only.
                Waiting periods, disease lists and exclusions are legally identical across all variants.
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-4">
          {plan.plan_variants?.map((v, i) => (
            <motion.div
              key={v.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => onChoose(v.id)}
              className={`glass-card p-5 flex flex-col cursor-pointer transition-colors hover:border-[#D4A017]/60 ${v.highlight ? "border-[#D4A017]/50 bg-[#D4A017]/5" : ""}`}
              data-testid={`health-choose-variant-${v.id}`}
            >
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h3 className={`font-bold text-base ${v.highlight ? "text-[#A87608]" : "text-[#1B2A4A]"}`}>{v.name}</h3>
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${v.highlight ? "bg-[#D4A017]/20 text-[#A87608]" : "bg-[#EDF1F8] text-[#2E3B57]"}`}>{v.badge}</span>
              </div>
              <p className="text-[#2E3B57] text-sm leading-relaxed mb-3">{v.description}</p>
              <div className="space-y-1.5 mb-3">
                {v.key_features?.slice(0, 3).map((f, j) => (
                  <div key={j} className="flex items-start gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <p className="text-[#3A4557] text-xs">{f}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-auto pt-2">
                <p className="text-cyan-600 text-xs font-medium">Best for: {v.best_for}</p>
                <span className="inline-flex items-center gap-1.5 text-[#A87608] text-xs font-bold whitespace-nowrap">
                  Explore <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

// ============ MAIN COMPONENT ============
export default function HealthPlanDetail({ plan }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [searchParams, setSearchParams] = useSearchParams();
  const singleVariant = plan.plan_variants?.length === 1;
  const variantId = searchParams.get("variant") || (singleVariant ? plan.plan_variants[0].id : null);
  const variant = plan.plan_variants?.find(v => v.id === variantId);
  const vd = plan.variant_details?.[variantId];

  const switchVariant = (id) => {
    setSearchParams({ variant: id });
    setActiveTab("overview");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!variant || !vd) {
    return <VariantChooser plan={plan} onChoose={switchVariant} />;
  }

  const tabs = plan.maternity_spotlight
    ? [...HEALTH_TABS.slice(0, 3), MATERNITY_TAB, ...HEALTH_TABS.slice(3)]
    : HEALTH_TABS;

  const benefitPool = [...(plan.core_benefits || []), ...(plan.extra_benefit_pool || [])];
  const benefits = (vd.benefit_ids || []).map(id => {
    const base = benefitPool.find(b => b.id === id);
    return base ? { ...base, ...(vd.benefit_overrides?.[id] || {}) } : null;
  }).filter(Boolean);
  const story = vd.multiplier_story === "default" ? plan.multiplier_story : vd.multiplier_story;
  const riders = (plan.riders || []).filter(r => (vd.rider_ids || []).includes(r.id));
  const subLimits = (plan.sub_limits_watch || []).filter(s => !s.variants || s.variants.includes(variantId));
  const levers = (plan.permutations?.levers || []).filter(l => !l.variants || l.variants.includes(variantId));

  const heroStats = [
    { label: "Cover options", value: vd.si_range || `${plan.sum_insured_options?.[0]} - ${plan.sum_insured_options?.[plan.sum_insured_options.length - 1]}`, icon: Shield },
    { label: "Room rent", value: vd.room_rent || "At Actuals — no cap", icon: Building2 },
    { label: "Co-pay", value: "Zero, at any age", icon: Percent },
    ...(plan.irdai_data?.csr ? [{ label: "Claim Settlement Ratio", value: `${plan.irdai_data.csr}%`, icon: BadgeCheck }] : [{ label: "Plan variants", value: `${plan.plan_variants?.length || 8} options`, icon: Layers }])
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] pb-16" data-testid="health-plan-detail-page">
      {/* Header */}
      <header className="glass-nav sticky top-2 mx-2 sm:mx-6 z-50 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between rounded-xl sm:rounded-2xl">
        <div className="flex items-center gap-1 sm:gap-3">
          <Button variant="ghost" onClick={() => navigate("/")} className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40 px-2 sm:px-3" data-testid="health-home-button">
            <Home className="h-4 w-4" />
          </Button>
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40 px-2 sm:px-3" data-testid="health-back-button">
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline ml-2">Back</span>
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate(`/compare?plans=${plan.id}`)} className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40 px-2 sm:px-3" title="Compare" data-testid="health-compare-button">
            <Scale className="h-4 w-4" />
            <span className="hidden sm:inline ml-2">Compare</span>
          </Button>
          <a href="tel:+919510020907">
            <Button className="btn-gold h-8 sm:h-10 px-3 sm:px-5 text-xs sm:text-sm" data-testid="health-call-advisor-button">
              <Phone className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Talk to Advisor</span>
            </Button>
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />
          <div className="flex items-start gap-4 mb-4">
            <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-white p-2 flex items-center justify-center flex-shrink-0">
              <img src={plan.company_logo} alt={plan.company} className="max-h-full max-w-full object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-700 text-xs font-bold" data-testid="health-type-badge">
                  HEALTH INSURANCE
                </span>
                <span className="px-2.5 py-1 rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-700 text-xs font-bold" data-testid="health-variant-badge">{variant.badge}</span>
                <span className="text-[#3A4557] text-xs">{plan.company_short || plan.company}</span>
                {plan.uin && <span className="text-[#3A4557] text-[10px]">UIN: {plan.uin}</span>}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1B2A4A] mb-1" data-testid="health-plan-name">{variant.name}</h1>
              <p className="text-[#A87608] text-sm font-medium">{plan.name} family · {plan.tagline}</p>
            </div>
          </div>
          <p className="text-[#2E3B57] text-sm sm:text-base leading-relaxed mb-5 max-w-3xl" data-testid="health-variant-description">{variant.description}</p>
          {!singleVariant && (
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="health-variant-switcher-row">
            <span className="text-[#3A4557] text-xs">Viewing variant:</span>
            <select
              value={variantId}
              onChange={(e) => switchVariant(e.target.value)}
              className="bg-[#EDF1F8]/60 border border-[#C9D4E6] text-[#1B2A4A] text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#D4A017]/50 cursor-pointer"
              data-testid="health-variant-switcher"
            >
              {plan.plan_variants.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setSearchParams({}); window.scrollTo(0, 0); }}
              className="border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A] text-xs h-8"
              data-testid="health-all-variants-button"
            >
              <Layers className="h-3.5 w-3.5 mr-1.5" /> All {plan.plan_variants?.length || 8} variants
            </Button>
          </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {heroStats.map((s, i) => (
              <div key={i} className="p-3 rounded-xl bg-[#EDF1F8]/40 border border-[#C9D4E6]">
                <div className="flex items-center gap-2 mb-1">
                  <s.icon className="h-4 w-4 text-emerald-600" />
                  <p className="text-[#3A4557] text-xs">{s.label}</p>
                </div>
                <p className="text-[#1B2A4A] font-bold text-sm">{s.value}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Tabs */}
      <div className="sticky top-16 sm:top-20 z-40 bg-white/95 backdrop-blur-md py-2 border-b border-[#C9D4E6]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <SectionNav tabs={tabs} activeTab={activeTab} onChange={setActiveTab} testIdPrefix="health-tab" />
        </div>
      </div>

      {/* Tab Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.15 }}>
            {activeTab === "overview" && <OverviewTab plan={plan} />}
            {activeTab === "variants" && <VariantsTab plan={plan} currentVariantId={variantId} onSwitch={switchVariant} />}
            {activeTab === "benefits-4x" && <BenefitsTab plan={plan} variant={variant} benefits={benefits} story={story} />}
            {activeTab === "maternity" && <MaternityTab plan={plan} />}
            {activeTab === "coverage" && <CoverageTab plan={plan} vd={vd} variant={variant} subLimits={subLimits} />}
            {activeTab === "riders" && <RidersTab plan={plan} riders={riders} levers={levers} />}
            {activeTab === "waiting-lists" && <WaitingListsTab plan={plan} />}
            {activeTab === "exclusions" && <ExclusionsTab plan={plan} />}
            {activeTab === "premium" && <PremiumTab plan={plan} vd={vd} />}
            {activeTab === "claims" && <ClaimsTab plan={plan} />}
            {activeTab === "downloads" && <DownloadsTab plan={plan} vd={vd} variant={variant} />}
            {activeTab === "jargon" && <JargonTab plan={plan} />}
            {activeTab === "askme" && <AskAITab plan={plan} variant={variant} />}
          </motion.div>
        </AnimatePresence>

        {/* CTA */}
        <div className="glass-card p-6 text-center mt-8 bg-gradient-to-r from-emerald-500/5 to-[#D4A017]/10">
          <h3 className="text-xl font-bold text-[#1B2A4A] mb-2">Ready to protect your family's health & savings?</h3>
          <p className="text-[#3A4557] mb-4 text-sm">Get the right variant, right SI and right add-ons for your family — a 15-minute call saves years of regret.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="tel:+919510020907">
              <Button className="btn-gold" data-testid="health-cta-call-button">
                <Phone className="h-4 w-4 mr-2" />
                Talk to Advisor
              </Button>
            </a>
            <Button onClick={() => navigate(`/compare?plans=${plan.id}`)} variant="outline" className="border-[#C9D4E6] text-[#2E3B57]" data-testid="health-cta-compare-button">
              <Scale className="h-4 w-4 mr-2" />
              Compare Plans
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
